import DeleteConfirmationDialog from '@components/DeleteConfirmation';
import EstimationDialog from '@components/EstimationDialog';
import { CircularProgressWithLabel } from '@components/common/CircularProgressWithLabel';
import { useEntitlementError } from '@context/EntitlementContext';
import { useMixpanel } from '@context/MixpanelProvider';
import { useEstimateCredits } from '@hooks/postQueries';
import { useCheckCredits } from '@hooks/useCheckCredits';
import { useWorkspaceUsage } from '@hooks/getQueries';
import useConfirmationDialog from '@hooks/useDeleteConfirmation';
import useWorkspace from '@hooks/useWorkspace';
import { Box, Link, Stack, Tab, Tabs, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles'; // Add this import
import { INTERVIEW_UPLOAD_EVENTS } from '@utils/eventSchema';
import { showErrorToast, showSuccessToast } from '@utils/toast';
import axios from 'axios';
import { Upload } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import toast from 'react-hot-toast';
import { useParams } from 'react-router-dom'; // Add this import at the top
import {
  useMultiCommitBlocks,
  useMultiGenerateUploadUrl,
  useSaveAndAnalyzeInterviewAudioVideo,
} from '../../../hooks/postQueries';
import { DEFAULT_REDACTION_POLICIES } from '@constants/transcriptWorkflows';
import { categorizeFileType } from './CategorizeFileType';
import { ConfigureDialog } from './ConfigureDialog';
import DocumentUploadTab from './DocumentUploadTab';
import { PiiRedactionControls } from './PiiRedactionControls';
import TranscriptWordLimitModal from './TranscriptWordLimitModal';
import {
  TRANSCRIPT_WORD_LIMIT,
  TRANSCRIPT_WORD_LIMIT_CONTACT_URL,
  countWordsInText,
  extractTranscriptWordLimitDetail,
  isTranscriptWordLimitError,
} from './transcriptWordLimit';
import UploadHeader from './UploadHeader';
import UploadedFiles from './UploadedFiles';

const FILE_SIZE_LIMITS = {
  text: 50 * 1024 * 1024, // 50 MB for text
  audio: 500 * 1024 * 1024, // 500 MB for audio
  video: 2 * 1024 * 1024 * 1024, // 2 GB for video
};
const MAX_FILES_PER_TAB = {
  text: 20,
  audio: 10,
  video: 10,
  // Matches MAX_FILES_PER_BATCH inside DocumentUploadTab — keep in sync.
  // Without this entry, the modal header computed `undefined - 0 = NaN`
  // for the Document tab and rendered "You can add NaN files now".
  document: 10,
};
// Per-tab dropzone accept config. react-dropzone v14 requires the
// `{ 'mime/type': ['.ext', ...] }` object form — a plain array is silently
// treated as "accept everything", which previously let users pick mp3 on the
// Text tab and broke the customer-api batch-routing contract (the backend
// routes a whole batch by the first file's MIME — see transcript_queue.py).
// Indexes here MUST match the Tab order below: 0 Text, 1 Audio, 2 Video.
const ACCEPT_BY_TAB = [
  {
    'text/plain': ['.txt'],
    'application/pdf': ['.pdf'],
    'application/msword': ['.doc'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  },
  {
    'audio/*': ['.mp3', '.wav', '.ogg', '.m4a', '.flac', '.aac', '.mpeg'],
  },
  {
    'video/*': ['.mp4', '.webm', '.avi', '.mov', '.mkv'],
  },
];

const TAB_LABELS = ['text', 'audio', 'video', 'document'];
const defaultOnFilesReady = () => {};
const formatFileCount = (count) => `${count} ${count === 1 ? 'file' : 'files'}`;

/** Internal tab key → short strings for toasts (avoid "20 files text files") */
const TAB_TOAST_LABELS = {
  text: { kind: 'text files', title: 'Text' },
  audio: { kind: 'audio files', title: 'Audio' },
  video: { kind: 'video files', title: 'Video' },
  document: { kind: 'documents', title: 'Document' },
};

const tabKindLabel = (tabKey) => TAB_TOAST_LABELS[tabKey]?.kind ?? 'files';
const tabShortTitle = (tabKey) => TAB_TOAST_LABELS[tabKey]?.title ?? tabKey;
/** Dropzone rejected the pick because too many files were selected at once */
const messageTooManyFilesInSelection = (tabKey, perBatchLimit, remainingSlots) => {
  const kind = tabKindLabel(tabKey);
  if (remainingSlots <= 0) {
    return `Batch full (${perBatchLimit} ${kind} max). Submit or remove files, then try again.`;
  }
  return `Too many files at once. You can add ${remainingSlots} more here (${perBatchLimit} ${kind} max per batch). Pick fewer or upload in steps.`;
};

/** Accepted file count would exceed the remaining slots in this batch */
const messageBatchWouldExceedCap = (tabKey, perBatchLimit) => {
  const kind = tabKindLabel(tabKey);
  return `That exceeds this batch (${perBatchLimit} ${kind} max). Remove files or submit first.`;
};

/** User clicked the drop zone but the batch is already at the per-tab cap */
const messageBatchFullClick = (tabKey, perBatchLimit) => {
  const kind = tabKindLabel(tabKey);
  return `Batch full (${perBatchLimit} ${kind} max). Submit or remove files to add more.`;
};

const FileUpload = ({
  onClose,
  width = '556px',
  interviewId,
  onFilesReady = defaultOnFilesReady,
  showHeader = true,
}) => {
  const theme = useTheme();
  const params = useParams();

  const id = params.id || interviewId;

  const [showEstimationDialog, setShowEstimationDialog] = useState(false);
  const { data: estimateCredits } = useEstimateCredits();
  const processId = estimateCredits?.process_id;
  const [currentTab, setCurrentTab] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [pendingUploads, setPendingUploads] = useState([]);
  const [uploadingFiles, setUploadingFiles] = useState([]);
  const [totalProgress, setTotalProgress] = useState(0);
  const [configurationData, setConfigurationData] = useState({
    language: '',
    selectedOptions: [],
  });

  // PII redaction state — tab-level, mirrors DocumentUploadTab's
  // pattern. Rendered inline below the dropzone for Text/Audio/Video
  // via PiiRedactionControls so the option is visible BEFORE the user
  // stages any files. Values are read directly by the payload builders
  // at upload time (handleDone / handleAnalyzeFiles / useEffect),
  // NOT round-tripped through configurationData — that used to be the
  // source of the ConfigureDialog coupling.
  const [redactPii, setRedactPii] = useState(false);
  const [redactionSubstitution, setRedactionSubstitution] = useState('entity_name');
  const [redactionPolicies, setRedactionPolicies] = useState([...DEFAULT_REDACTION_POLICIES]);

  const togglePolicy = (policyValue) =>
    setRedactionPolicies((prev) =>
      prev.includes(policyValue) ? prev.filter((p) => p !== policyValue) : [...prev, policyValue],
    );
  const { trackEvent } = useMixpanel();
  const { open, show, handleConfirm, handleClose } = useConfirmationDialog();
  const [creditsNeeded] = useState(null);
  const { NoCreditDialog } = useCheckCredits(creditsNeeded);
  const { activeWorkspaceId } = useWorkspace();
  const { data: workspaceUsage } = useWorkspaceUsage(activeWorkspaceId);
  const { showInsufficientEntitlementsError, handleApiError } = useEntitlementError();
  const remainingUploadsRaw = workspaceUsage?.entitlements?.transcription_uploads_remaining;
  const remainingUploads = typeof remainingUploadsRaw === 'number' ? remainingUploadsRaw : null;
  const currentTabLabel = TAB_LABELS[currentTab];
  const currentTabLimit = MAX_FILES_PER_TAB[currentTabLabel];
  const stagedUploads = [...pendingUploads, ...uploadedFiles];
  const stagedUploadCount = stagedUploads.length;
  const stagedCurrentTabCount = stagedUploads.filter(
    (file) => categorizeFileType(file?.type || '') === currentTabLabel,
  ).length;
  const remainingBatchSlots = Math.max(currentTabLimit - stagedCurrentTabCount, 0);
  const remainingEntitlementSlots =
    remainingUploads !== null ? Math.max(remainingUploads - stagedUploadCount, 0) : Infinity;
  const availableUploadSlots = Math.min(remainingBatchSlots, remainingEntitlementSlots);
  const hasNoEntitlementSlots = remainingUploads !== null && remainingEntitlementSlots <= 0;
  const hasNoAvailableUploads = availableUploadSlots <= 0;
  const [uploadedFilesProgressCount, setUploadedFilesProgressCount] = useState(0);

  // Word-limit modal state — driven from two places:
  //   1. Client-side .txt pre-check in onDrop (fast, no network round-trip).
  //   2. Server-side 413 from save_and_transcript_generate for pdf/doc/docx
  //      (checked in the paired customer-api endpoint after the upload commit).
  // Both paths converge on the same modal so the customer sees one consistent
  // "contact us" screen regardless of which file type tripped the cap.
  const [wordLimitModalOpen, setWordLimitModalOpen] = useState(false);
  const [wordLimitDetail, setWordLimitDetail] = useState({
    limit: TRANSCRIPT_WORD_LIMIT,
    overLimitFiles: [],
    contactUrl: TRANSCRIPT_WORD_LIMIT_CONTACT_URL,
  });

  const generateMultipleUploadUrl = useMultiGenerateUploadUrl();
  const multiCommitBlocks = useMultiCommitBlocks();
  const saveAndAnalyzeInterviewAudioVideo = useSaveAndAnalyzeInterviewAudioVideo();
  const BLOCK_SIZE = 4 * 1024 * 1024; // 4MB

  useEffect(() => {
    // Track initial upload screen view
    trackEvent('interviewupload', {
      ...INTERVIEW_UPLOAD_EVENTS.INTERVIEW_VIEWED_UPLOAD_SCREEN,
    });
  }, []);

  const splitFileIntoBlocks = (file) => {
    const blocks = [];
    let start = 0;
    while (start < file.size) {
      const end = Math.min(start + BLOCK_SIZE, file.size);
      blocks.push(file.slice(start, end));
      start = end;
    }
    return blocks;
  };

  const generateBlockId = (index) => {
    return btoa(`${index.toString().padStart(32, '0')}`);
  };

  const uploadBlock = async (block, blockId, uploadUrl) => {
    const blockUrl = `${uploadUrl}&comp=block&blockid=${blockId}`;

    await axios.put(blockUrl, block, {
      headers: {
        'Content-Type': block.type,
        'x-ms-blob-type': 'BlockBlob',
      },
      onUploadProgress: () => {},
    });
  };

  const handleBatchUpload = async (files) => {
    try {
      setIsUploading(true);
      setUploadingFiles(files);

      const uploadRequests = files.map((file) => ({
        filename: file.name,
        content_type: file.type,
        file_size: file.size,
      }));

      const payload = {
        uploads: uploadRequests,
        study_id: id,
      };
      // console.log('the payload in handleBatchUpload is:', payload);
      const uploadUrlResponse = await generateMultipleUploadUrl.mutateAsync(payload);

      const uploadResults = [];
      const commitRequests = [];

      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const uploadInfo = uploadUrlResponse.uploads[i];
        // console.log('Upload info:', uploadInfo);

        const blocks = splitFileIntoBlocks(file);
        const blockIds = [];

        for (let j = 0; j < blocks.length; j++) {
          const blockId = generateBlockId(j);
          blockIds.push(blockId);
          await uploadBlock(
            blocks[j],
            blockId,
            uploadInfo?.upload_data?.upload_url,
            j,
            blocks.length,
          );
          setTotalProgress((i * 100 + ((j + 1) * 100) / blocks.length) / files.length);
        }
        setUploadedFilesProgressCount((prev) => prev + 1);
        commitRequests.push({
          blob_name: file.name,
          block_ids: blockIds,
          content_type: file.type,
          upload_url: uploadInfo?.upload_data?.upload_url,
        });

        // Store file info temporarily without status
        uploadResults.push({
          name: file.name,
          path: file.name, // keep both name and path for compatibility
          size: file.size,
          type: file.type,
          lastModified: file.lastModified,
          blob_url: uploadInfo?.upload_data?.upload_url,
        });
      }

      const commitResult = await multiCommitBlocks.mutateAsync({
        uploads: commitRequests,
      });
      setUploadedFilesProgressCount(0);
      // Update uploadResults with status from commitResult
      const finalUploadResults = uploadResults.map((file) => {
        const commitData = commitResult.results.find((result) => result.blob_name === file.name);
        return {
          ...file,
          status: commitData?.status || 'failed', // Add status from commit result
          url: commitData?.url || '', // Optionally store the final URL
        };
      });

      setPendingUploads(finalUploadResults);
      return true;
    } catch (error) {
      // console.error('Error details while uploading files:', error);
      if (handleApiError(error)) {
        return false;
      }
      const detailMessage = Array.isArray(error?.detail)
        ? error.detail[0]?.msg
        : error?.detail?.message || error?.detail || error?.message || 'Please try again.';
      showErrorToast(`Failed to upload files: ${detailMessage}`);
      return false;
    } finally {
      setIsUploading(false);
      setUploadingFiles([]);
      setTotalProgress(0);
    }
  };

  const onDrop = useCallback(
    async (acceptedFiles, fileRejections = []) => {
      if (isUploading) {
        return;
      }

      const requestedFileCount = acceptedFiles.length + fileRejections.length;
      const rejectedForFileCount = fileRejections.some((rejection) =>
        rejection.errors?.some((error) => error.code === 'too-many-files'),
      );

      if (rejectedForFileCount) {
        if (remainingUploads !== null && requestedFileCount > remainingEntitlementSlots) {
          showInsufficientEntitlementsError({
            entitlementType: 'transcription_uploads',
            required: requestedFileCount,
            available: remainingEntitlementSlots,
          });
          return;
        }

        showErrorToast(
          messageTooManyFilesInSelection(currentTabLabel, currentTabLimit, remainingBatchSlots),
        );
        return;
      }

      trackEvent('interviewupload', {
        ...INTERVIEW_UPLOAD_EVENTS.INTERVIEW_FILE_UPLOAD_STARTED,
        file_count: acceptedFiles.length,
        file_types: acceptedFiles.map((file) => file.type).join(', '),
        file_sizes: acceptedFiles.map((file) => file.size).join(', '),
      });

      if (remainingUploads !== null && acceptedFiles.length > remainingEntitlementSlots) {
        showInsufficientEntitlementsError({
          entitlementType: 'transcription_uploads',
          required: acceptedFiles.length,
          available: remainingEntitlementSlots,
        });
        return;
      }

      if (acceptedFiles.length > remainingBatchSlots) {
        showErrorToast(messageBatchWouldExceedCap(currentTabLabel, currentTabLimit));
        return;
      }

      if (!acceptedFiles?.length) {
        if (fileRejections.length > 0) {
          const firstError = fileRejections[0]?.errors?.find(Boolean);
          if (firstError?.code === 'file-too-large') {
            const tabLimitLabel =
              currentTab === 0
                ? '50MB'
                : currentTab === 1
                  ? '500MB'
                  : currentTab === 2
                    ? '2GB'
                    : '';
            showErrorToast(
              `One or more files exceed the size limit for ${tabShortTitle(currentTabLabel)} uploads (${tabLimitLabel}).`,
            );
            return;
          }
          if (firstError?.code === 'file-invalid-type') {
            showErrorToast(
              `One or more files are not a supported type on the ${tabShortTitle(currentTabLabel)} tab.`,
            );
            return;
          }
          showErrorToast(firstError?.message || 'Some files could not be added.');
          return;
        }
        toast.error('No files received');
        return;
      }

      // Belt-and-suspenders: enforce that file MIME matches the active tab.
      // The dropzone's ``accept`` filter already does this for the file
      // picker, but drag-and-drop in some browsers reports an empty MIME and
      // sneaks through. The backend routes a whole batch by the first file's
      // MIME (see transcript_queue.py), so a mismatch here causes silent
      // transcription failures downstream.
      const expectedTab = TAB_LABELS[currentTab];
      const mismatched = acceptedFiles.filter((file) => {
        const fileCategory = categorizeFileType(file.type || '');
        return fileCategory !== expectedTab;
      });
      if (mismatched.length > 0) {
        showErrorToast(
          `${formatFileCount(mismatched.length)} did not match the ${expectedTab} tab. Switch to the matching tab or remove these files: ${mismatched
            .map((f) => f.name)
            .join(', ')}`,
        );
        return;
      }

      // Filter out files larger than their respective limits
      const validFiles = acceptedFiles.filter((file) => {
        const sizeLimit =
          currentTab === 0
            ? FILE_SIZE_LIMITS.text
            : currentTab === 1
              ? FILE_SIZE_LIMITS.audio
              : FILE_SIZE_LIMITS.video;

        if (file.size > sizeLimit) {
          const limitInMB = sizeLimit / (1024 * 1024);
          showErrorToast(`File ${file.name} exceeds ${limitInMB}MB limit`);
          return false;
        }
        return true;
      });

      // Client-side pre-check: reject .txt files that already exceed the
      // per-file word cap without ever hitting the network. The whole
      // file is read into memory here, but the file-size guard above
      // already capped it at 50 MB. PDF/DOC/DOCX are checked server-side
      // (see customer-api's `save_and_transcript_generate` — pulling in
      // a browser PDF/DOCX parser here would balloon the bundle for a
      // check the backend has to run anyway).
      const txtFiles = validFiles.filter((file) => file.type === 'text/plain');
      let filesToUpload = validFiles;
      if (txtFiles.length > 0) {
        const counted = await Promise.all(
          txtFiles.map(async (file) => {
            try {
              const text = await file.text();
              return { file, wordCount: countWordsInText(text) };
            } catch {
              // Unreadable text file — let the upload go through so the
              // server can surface a real error. Same fail-open reasoning
              // as the backend helper.
              return { file, wordCount: 0 };
            }
          }),
        );
        const overLimit = counted.filter(({ wordCount }) => wordCount > TRANSCRIPT_WORD_LIMIT);
        if (overLimit.length > 0) {
          setWordLimitDetail({
            limit: TRANSCRIPT_WORD_LIMIT,
            overLimitFiles: overLimit.map(({ file, wordCount }) => ({
              filename: file.name,
              wordCount,
            })),
            contactUrl: TRANSCRIPT_WORD_LIMIT_CONTACT_URL,
          });
          setWordLimitModalOpen(true);
          const rejected = new Set(overLimit.map(({ file }) => file));
          filesToUpload = validFiles.filter((f) => !rejected.has(f));
        }
      }

      if (filesToUpload.length > 0) {
        handleBatchUpload(filesToUpload);
      }
    },
    [
      isUploading,
      uploadedFiles,
      currentTab,
      pendingUploads,
      remainingUploads,
      remainingBatchSlots,
      currentTabLimit,
      currentTabLabel,
      remainingEntitlementSlots,
      showInsufficientEntitlementsError,
    ],
  );

  // Modify dropzone config to accept multiple files
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    maxSize:
      currentTab === 0
        ? FILE_SIZE_LIMITS.text
        : currentTab === 1
          ? FILE_SIZE_LIMITS.audio
          : FILE_SIZE_LIMITS.video,
    accept: ACCEPT_BY_TAB[currentTab],
    disabled: isUploading || availableUploadSlots <= 0,
    multiple: true, // Enable multiple file selection
    maxFiles: availableUploadSlots,
    onError: (err) => {
      toast.error('Dropzone error: ' + err.message);
    },
  });

  const handleRemoveFile = (fileId) => {
    setUploadedFiles((files) => files.filter((f) => f.id !== fileId));
  };

  const handleConfigureFiles = (config) => {
    setConfigurationData(config);
    const featureFlags = {
      summarization: false,
      auto_chapters: false,
      important_phrases: false,
      entity_detection: false,
      topic_detection: false,
      profanity_filtering: false,
    };

    // Enable the selected options
    config.selectedOptions.forEach((option) => {
      const key = option.toLowerCase().replace(/\s+/g, '_');
      if (key in featureFlags) {
        featureFlags[key] = true;
      }
    });
    const newFiles = pendingUploads.map((file) => ({
      id: Math.random().toString(36).substr(2, 9),
      name: file.name,
      path: file.name,
      size: file.size,
      type: file.type,
      lastModified: file.lastModified,
      blob_url: file.blob_url,
      status: file.status,
      url: file.url,
      language_code: config.language,
      ...featureFlags,
    }));

    setUploadedFiles((prev) => [...prev, ...newFiles]);
    setPendingUploads([]);

    trackEvent('interviewupload', {
      ...INTERVIEW_UPLOAD_EVENTS.INTERVIEW_LANGUAGE_SELECTED,
      language: config.language,
    });
    trackEvent('interviewupload', {
      ...INTERVIEW_UPLOAD_EVENTS.INTERVIEW_FILE_ADDED,
      file_count: newFiles.length,
      file_type: newFiles[0].type.split('/')[0], // Assuming all files are of the same type
      language: config.language,
    });
  };

  const handleRemoveConfigFile = (fileToRemove) => {
    setPendingUploads(pendingUploads.filter((file) => file.name !== fileToRemove.name));
  };
  const handleEstimationAgree = async () => {
    if (uploadedFiles.length === 0) return;

    const payload = {
      blob_info: uploadedFiles.map((file) => ({
        blob_name: file?.name,
        url: file?.url,
        status: file?.status,
        type: categorizeFileType(file?.type),
        file_content_type: file?.type,
        country: configurationData.language,
        is_transcribed: false,
      })),
      language_code: configurationData.language,
      ...(redactPii && {
        redact_pii: true,
        redaction_substitution: redactionSubstitution,
        redaction_policy_names: redactionPolicies,
      }),
    };

    try {
      const response = await saveAndAnalyzeInterviewAudioVideo.mutateAsync({
        id,
        processId,
        ...payload,
      });
      if (response) {
        trackEvent('interviewupload', {
          ...INTERVIEW_UPLOAD_EVENTS.INTERVIEW_FILE_UPLOAD_COMPLETED,
          file_count: uploadedFiles.length,
          file_types: uploadedFiles.map((file) => file.type).join(', '),
        });
      }
      showSuccessToast(
        "Your interview data has been uploaded. We'll notify you as soon as the transcript is ready",
        '500px',
      );
      onClose();
    } catch (error) {
      if (isTranscriptWordLimitError(error)) {
        setWordLimitDetail(extractTranscriptWordLimitDetail(error));
        setWordLimitModalOpen(true);
        return;
      }
      if (handleApiError(error)) {
        return;
      }
      showErrorToast('Failed to process files');
    }
  };

  // Use the mutation's isPending state for reliable loading indication
  const isSubmitting = saveAndAnalyzeInterviewAudioVideo.isPending;

  const handleDone = async () => {
    // Prevent double-click by checking if already submitting
    if (uploadedFiles.length === 0 || isSubmitting) return;

    const payload = {
      blob_info: uploadedFiles.map((file) => ({
        blob_name: file?.name,
        url: file?.url,
        status: file?.status,
        type: categorizeFileType(file?.type),
        file_content_type: file?.type,
        country: configurationData.language,
        is_transcribed: false,
      })),
      language_code: configurationData.language,
      ...(redactPii && {
        redact_pii: true,
        redaction_substitution: redactionSubstitution,
        redaction_policy_names: redactionPolicies,
      }),
    };

    // Pass the payload to parent component directly
    onFilesReady(payload);

    try {
      await saveAndAnalyzeInterviewAudioVideo.mutateAsync({
        id,
        processId: processId ?? '',
        ...payload,
      });

      trackEvent('interviewupload', {
        ...INTERVIEW_UPLOAD_EVENTS.INTERVIEW_FILE_UPLOAD_COMPLETED,
        file_count: uploadedFiles.length,
        file_types: uploadedFiles.map((file) => file.type).join(', '),
      });
      showSuccessToast(
        "Your interview data has been uploaded. We'll notify you as soon as the transcript is ready",
      );
      onClose();
    } catch (error) {
      if (isTranscriptWordLimitError(error)) {
        setWordLimitDetail(extractTranscriptWordLimitDetail(error));
        setWordLimitModalOpen(true);
        return;
      }
      if (handleApiError(error)) {
        return;
      }
      showErrorToast('Failed to process files');
    }
  };

  const handleCancelUpload = () => {
    show(() => {
      onClose();
    });
  };

  useEffect(() => {
    // Only call onFilesReady if it's not the default function
    if (onFilesReady !== defaultOnFilesReady) {
      if (uploadedFiles.length > 0) {
        const payload = {
          blob_info: uploadedFiles.map((file) => ({
            blob_name: file?.name,
            url: file?.url,
            status: file?.status,
            type: categorizeFileType(file?.type),
            file_content_type: file?.type,
            country: configurationData.language,
            is_transcribed: false,
          })),
          language_code: configurationData.language,
          ...(redactPii && {
            redact_pii: true,
            redaction_substitution: redactionSubstitution,
            redaction_policy_names: redactionPolicies,
          }),
        };
        onFilesReady(payload);
      } else {
        onFilesReady(null);
      }
    }
    // PII state included so the memoised payload rebuilds when the tab-level
    // toggle/policy set flips — otherwise the parent onFilesReady consumer
    // would stay on a stale payload after the user changed the redaction
    // config without touching the file list.
  }, [uploadedFiles, configurationData, redactPii, redactionSubstitution, redactionPolicies]);

  const getFileSizeLimitText = () => {
    switch (currentTab) {
      case 0:
        return '50MB';
      case 1:
        return '500MB';
      case 2:
        return '2GB';
      default:
        return '50MB';
    }
  };

  const uploadingContent = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%', // Use full height of parent
        width: '100%', // Use full width of parent
        gap: 1,
        padding: 2,
      }}
    >
      <CircularProgressWithLabel variant="determinate" value={totalProgress} />
      <Typography
        variant="body1"
        sx={{
          textAlign: 'center',
        }}
      >
        Uploading {`${uploadedFilesProgressCount}/${uploadingFiles.length}`}
        {uploadingFiles.length === 1 ? ' file' : ' files'}...
      </Typography>

      <Typography
        variant="body2"
        color="text.secondary"
        sx={{
          textAlign: 'center',
          fontSize: {
            xs: '0.75rem',
            sm: '0.875rem',
          },
        }}
      >
        This process may take a while, depending on the file sizes.
      </Typography>
    </Box>
  );

  return (
    <>
      <Box
        sx={{
          backgroundColor: 'white',
          width: { xs: '90%', sm: '90%', md: width },
          maxHeight: 'calc(100vh - 32px)',
          maxWidth: width,
          mx: 'auto',
          padding: '0px',
          border: '2px solid #E8E8E8',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            overflow: 'auto',
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Header component */}
          {showHeader && (
            <UploadHeader
              onCancel={handleCancelUpload}
              onDone={handleDone}
              hasFiles={uploadedFiles.length > 0}
              isSubmitting={isSubmitting}
            />
          )}

          {/* Description and tabs sections remain the same */}
          <Stack
            id="parent-modal-description"
            spacing={1}
            sx={{
              px: 2,
              py: 1,
              pt: showHeader ? 1 : 3,
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                fontSize: { xs: '12px', sm: '14px' },
                fontWeight: 400,
              }}
            >
              Upload one file per interview or focus group discussion (FGD). You can upload up to{' '}
              {MAX_FILES_PER_TAB.text} text, {MAX_FILES_PER_TAB.audio} audio, or{' '}
              {MAX_FILES_PER_TAB.video} video files per batch.
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                fontSize: { xs: '12px', sm: '14px' },
                fontWeight: 400,
              }}
            >
              {remainingUploads !== null ? (
                <>
                  This workspace has {formatFileCount(remainingUploads)} remaining. You can add{' '}
                  {formatFileCount(availableUploadSlots)} now.{' '}
                </>
              ) : (
                <>You can add {formatFileCount(availableUploadSlots)} now. </>
              )}
              Need more?{' '}
              <Link
                href="https://qualz.ai/contactus"
                target="_blank"
                rel="noopener noreferrer"
                sx={{ cursor: 'pointer', textDecoration: 'none' }}
              >
                Contact us
              </Link>
              .
            </Typography>
          </Stack>

          {/* Tabs section */}
          <Box
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              height: '48px',
              // backgroundColor: 'red',
              ml: 2,
              mr: 2,
              mb: {
                xs: 1,
                sm: 2,
                md: 3,
              },
            }}
          >
            <Tabs
              value={currentTab}
              onChange={(_, value) => setCurrentTab(value)}
              sx={{
                '& .MuiTab-root': {
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  minWidth: 'auto', // Prevents full-width tabs
                },
                '& .MuiTabs-indicator': {
                  height: '0.188rem', // Thicker border
                  width: '40%', // Adjusts the indicator width
                  left: '30%', // Centers it under the tab
                },
              }}
            >
              {/* Implement text later */}
              <Tab label="Text" sx={{ padding: '0px', mr: 3 }} />
              <Tab label="Audio" sx={{ padding: '0px', mr: 3 }} />
              <Tab label="Video" sx={{ padding: '0px', mr: 3 }} />
              {/*
                Document tab — non-conversational uploads (PDF/DOCX/TXT)
                routed through the unified Qualitative_Items endpoint.
                Existing Text/Audio/Video flow is byte-identical.
              */}
              <Tab label="Document" sx={{ padding: '0px' }} />
            </Tabs>
          </Box>

          {/*
            Tab content: tabs 0/1/2 (Text/Audio/Video) use the existing
            dropzone + dialog flow below. Tab 3 (Document) is a
            self-contained side-by-side path that POSTs directly to the
            unified qualitative-items/upload-document endpoint — no
            block upload, no estimation, no diarisation. We don't touch
            the existing flow when on tab 3; we just render its
            replacement instead.
          */}
          {currentTab === 3 ? (
            <DocumentUploadTab studyId={id} onClose={onClose} />
          ) : (
            <>
              {/* Tab-level PII redaction — always visible on
                  Text/Audio/Video so users see the option BEFORE
                  dropping files. Backend contract unchanged: values
                  fold into the same redact_pii / redaction_substitution
                  / redaction_policy_names fields on the analyze
                  payload. See DevOps #2184. */}
              <Box sx={{ mx: 2, mb: 2 }}>
                <PiiRedactionControls
                  redactPii={redactPii}
                  onRedactPiiChange={setRedactPii}
                  redactionSubstitution={redactionSubstitution}
                  onSubstitutionChange={setRedactionSubstitution}
                  redactionPolicies={redactionPolicies}
                  onTogglePolicy={togglePolicy}
                  disabled={isUploading}
                  description="Automatically remove personal information during transcription."
                  variant="card"
                />
              </Box>
              <Box
                {...getRootProps()}
                onClick={(e) => {
                  if (hasNoAvailableUploads) {
                    e.preventDefault();
                    e.stopPropagation();
                    if (hasNoEntitlementSlots) {
                      showInsufficientEntitlementsError({
                        entitlementType: 'transcription_uploads',
                        required: 1,
                        available: 0,
                      });
                    } else {
                      showErrorToast(messageBatchFullClick(currentTabLabel, currentTabLimit));
                    }
                    return;
                  }
                  // console.log('Dropzone clicked');
                  getRootProps().onClick(e);
                }}
                sx={{
                  border: '2px dashed',
                  borderColor: isDragActive ? 'primary.main' : 'grey.300',
                  borderRadius: 1,
                  minHeight: { xs: 208, sm: 224 },
                  textAlign: 'center',
                  bgcolor: 'grey.50',
                  cursor: isUploading || availableUploadSlots <= 0 ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mx: 2,
                  mb: 2,
                  flexShrink: 0,
                }}
              >
                <input {...getInputProps()} />
                {isUploading ? (
                  uploadingContent
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%',
                    }}
                  >
                    <Upload size={16} color={theme.palette.primary.main} sx={{ mb: 1 }} />
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: '600',
                        fontSize: {
                          xs: '0.9rem',
                          sm: '1rem',
                        },
                        px: {
                          xs: 1,
                          sm: 2,
                        },
                      }}
                    >
                      {availableUploadSlots > 0
                        ? `Drag and drop or click to upload ${
                            currentTab === 0 ? 'text' : currentTab === 1 ? 'audio' : 'video'
                          } files`
                        : 'No upload slots available right now'}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="#6B6B6B"
                      sx={{
                        fontSize: {
                          xs: '0.75rem',
                          sm: '0.875rem',
                        },
                      }}
                    >
                      Supports all{' '}
                      {currentTab === 0
                        ? 'text, docx & pdf '
                        : currentTab === 1
                          ? 'mp3, mpeg, wav, ogg, m4a, flac & aac '
                          : 'mp4, webm, avi, mov & mkv '}{' '}
                      formats.
                    </Typography>
                    <Typography
                      variant="body2"
                      color="#6B6B6B"
                      sx={{
                        fontSize: {
                          xs: '0.75rem',
                          sm: '0.875rem',
                        },
                      }}
                    >
                      Up to {getFileSizeLimitText()} per file.
                    </Typography>
                  </Box>
                )}
              </Box>
            </>
          )}
        </Box>

        {/* Configure Dialog */}
        <ConfigureDialog
          currentTab={currentTab}
          open={pendingUploads.length > 0}
          files={pendingUploads}
          onClose={() => setPendingUploads([])}
          onAdd={handleConfigureFiles}
          onRemoveFile={handleRemoveConfigFile}
        />
      </Box>

      {/* Uploaded files component */}
      <UploadedFiles files={uploadedFiles} onRemoveFile={handleRemoveFile} width={width} />

      {/* Dialogs */}
      <NoCreditDialog />
      <EstimationDialog
        open={showEstimationDialog}
        onClose={() => setShowEstimationDialog(false)}
        onAgree={handleEstimationAgree}
        title="This action requires additional entitlements"
        description="This will incur additional entitlements."
        estimateCredits={estimateCredits}
      />
      <DeleteConfirmationDialog
        open={open}
        onClose={handleClose}
        onConfirm={handleConfirm}
        okText="Yes"
        noText="No"
        title="Cancel Upload"
        content="Your progress will be lost. Do you wish to cancel?"
      />
      <TranscriptWordLimitModal
        open={wordLimitModalOpen}
        limit={wordLimitDetail.limit}
        overLimitFiles={wordLimitDetail.overLimitFiles}
        contactUrl={wordLimitDetail.contactUrl}
        onClose={() => setWordLimitModalOpen(false)}
      />
    </>
  );
};

export default FileUpload;
